import Link from '../components/LocalizedLink'
import { motion } from 'framer-motion'
import {
  Calculator,
  FileText,
  Zap,
  DollarSign,
  Download,
  Shield,
  TrendingUp,
  Users,
  Clock,
  Check,
  ArrowRight,
  Factory,
} from 'lucide-react'
import { useLanguage } from '../contexts/LanguageContext'
import SEO from '../components/SEO'
import LiveStats, { CountUp, useLiveStats } from '../components/LiveStats'

// Maps each homepage feature card (by index, see `features` arrays below) to
// its dedicated landing page, where one exists — turns the card into a link
// and sends homepage link equity to the new keyword-targeted pages.
const featureLinks = ['/production-costing', '/dxf-step-import', null, null, null, null]

const translations = {
  el: {
    hero: {
      title: 'Πρόγραμμα Κοστολόγησης',
      highlight: ' Λαμαρίνας',
      subtitle: 'Κοστολογήστε λαμαρίνα, κοπή laser και στράντζα σε δευτερόλεπτα. Δημιουργήστε επαγγελματικές προσφορές. Κερδίστε περισσότερους πελάτες. Το Workshop Assistant είναι η ολοκληρωμένη λύση για εργαστήρια μεταλλουργίας.',
      cta1: 'Δωρεάν Δοκιμή',
      cta2: 'Δείτε τις Λειτουργίες',
      badge1: '5 Ημέρες Δωρεάν Δοκιμή',
      badge2: 'Χωρίς Πιστωτική Κάρτα',
      badge3: 'Πλήρεις Λειτουργίες',
      stat: 'Προσφορές Δημιουργήθηκαν',
    },
    featuresSection: {
      title: 'Ισχυρές Λειτουργίες για το Εργαστήριό σας',
      subtitle: 'Όλα όσα χρειάζεστε για να βελτιστοποιήσετε τη διαδικασία προσφορών και να αναπτύξετε την επιχείρησή σας.',
      guide: 'Οδηγός: πώς γίνεται η κοστολόγηση λαμαρίνας',
    },
    features: [
      { title: 'Αυτόματος Υπολογισμός Κόστους', description: 'Υπολογίστε άμεσα κόστη κατασκευής για εξαρτήματα ελάσματος και CNC router με ακρίβεια.' },
      { title: 'Εισαγωγή DXF & STEP', description: 'Εισάγετε αρχεία CAD απευθείας και εξάγετε γεωμετρικά δεδομένα αυτόματα.' },
      { title: 'Εξαιρετική Ταχύτητα', description: 'Δημιουργήστε επαγγελματικές προσφορές σε δευτερόλεπτα, όχι ώρες.' },
      { title: 'Παραμετροποιήσιμες Ρυθμίσεις', description: 'Ρυθμίστε κόστη μηχανημάτων, τιμές υλικών και markup για να ταιριάζουν με την επιχείρησή σας.' },
      { title: 'Επαγγελματικές Αναφορές', description: 'Δημιουργήστε λεπτομερείς αναφορές Excel με ανάλυση κόστους για πελάτες.' },
      { title: 'Ασφαλές & Αξιόπιστο', description: 'Desktop εφαρμογή χωρίς εξάρτηση από cloud. Τα δεδομένα σας παραμένουν στο μηχάνημά σας.' },
    ],
    benefitsSection: {
      title: 'Γιατί Workshop Assistant;',
      subtitle: 'Λιγότερος χρόνος στις προσφορές, περισσότερος στην παραγωγή.',
    },
    benefits: [
      { title: 'Αύξηση Κερδοφορίας', description: 'Βελτιστοποιήστε τη στρατηγική τιμολόγησης και μειώστε τον χρόνο προετοιμασίας προσφορών κατά 90%.', stat: '90%', statLabel: 'Εξοικονόμηση Χρόνου' },
      { title: 'Κερδίστε Περισσότερους Πελάτες', description: 'Απαντήστε σε αιτήματα προσφορών ταχύτερα από τους ανταγωνιστές με επαγγελματικές, λεπτομερείς προτάσεις.', stat: '3x', statLabel: 'Ταχύτερες Προσφορές' },
      { title: 'Εξοικονόμηση Χρόνου', description: 'Αυτοματοποιήστε επαναλαμβανόμενους υπολογισμούς και επικεντρωθείτε στην ανάπτυξη της επιχείρησής σας.', stat: '10ώρ', statLabel: 'Εξοικονόμηση/Εβδομάδα' },
    ],
    shopsSection: {
      title: 'Πραγματικά Νούμερα από Πραγματικά Εργαστήρια',
      subtitle: 'Πώς χρησιμοποιούν τα εργαστήρια το Workshop Assistant αυτή τη στιγμή.',
      shop: 'Μεταλλουργείο',
      perMonth: 'προσφορές / μήνα',
      offers: 'προσφορές',
      batches: 'παρτίδες',
      quoted: 'κοστολογημένα',
      valueQuoted: 'αξία προσφορών',
      weightQuoted: 'κοστολογημένο βάρος',
      using: (m) => `χρήση εδώ και ${m} ${m === 1 ? 'μήνα' : 'μήνες'}`,
      sheet: 'λαμαρίνα',
      and: 'και',
      note: 'Ανώνυμα στοιχεία από τα στατιστικά χρήσης της εφαρμογής, ενημερώνονται αυτόματα. Τα ονόματα των εργαστηρίων δεν δημοσιεύονται.',
    },
    cta: {
      title: 'Έτοιμοι να Μεταμορφώσετε το Εργαστήριό σας;',
      subtitle: 'Ξεκινήστε τη δωρεάν δοκιμή σήμερα. Χωρίς πιστωτική κάρτα. 5 ημέρες δωρεάν δοκιμή.',
      btn1: 'Λήψη Τώρα',
      btn2: 'Δείτε Τιμές',
    },
  },
  en: {
    hero: {
      title: 'Sheet Metal Costing',
      highlight: ' Software',
      subtitle: 'Sheet metal costing in seconds — material, laser cutting and bending, calculated from your DXF and STEP files. Generate professional quotes. Win more business. Workshop Assistant is the all-in-one solution for metal fabrication shops.',
      cta1: 'Download Free Trial',
      cta2: 'Explore Features',
      badge1: '5-Day Free Trial',
      badge2: 'No Credit Card',
      badge3: 'Full Features',
      stat: 'Quotes Generated',
    },
    featuresSection: {
      title: 'Powerful Features for Your Workshop',
      subtitle: 'Everything you need to streamline your quoting process and grow your business.',
      guide: 'Guide: how sheet metal costing works',
    },
    features: [
      { title: 'Automatic Cost Calculation', description: 'Instantly calculate manufacturing costs for sheet metal and CNC router parts with precision.' },
      { title: 'DXF & STEP Import', description: 'Import CAD files directly and extract geometric data automatically.' },
      { title: 'Lightning Fast', description: 'Generate professional quotes in seconds, not hours.' },
      { title: 'Customizable Parameters', description: 'Configure machine costs, material prices, and markup values to match your business.' },
      { title: 'Professional Reports', description: 'Generate detailed Excel reports with cost breakdowns for customers.' },
      { title: 'Secure & Reliable', description: 'Desktop application with no cloud dependency. Your data stays on your machine.' },
    ],
    benefitsSection: {
      title: 'Why Workshop Assistant?',
      subtitle: 'Less time on quotes, more time on production.',
    },
    benefits: [
      { title: 'Increase Profitability', description: 'Optimize pricing strategies and reduce quote preparation time by up to 90%.', stat: '90%', statLabel: 'Time Saved' },
      { title: 'Win More Customers', description: 'Respond to quote requests faster than competitors with professional, detailed proposals.', stat: '3x', statLabel: 'Faster Quotes' },
      { title: 'Save Time', description: 'Automate repetitive calculations and focus on growing your business.', stat: '10hrs', statLabel: 'Saved Per Week' },
    ],
    shopsSection: {
      title: 'Real Numbers from Real Workshops',
      subtitle: 'How fabrication shops are using Workshop Assistant right now.',
      shop: 'Fabrication shop',
      perMonth: 'offers / month',
      offers: 'offers',
      batches: 'batches',
      quoted: 'quoted',
      valueQuoted: 'value quoted',
      weightQuoted: 'weight quoted',
      using: (m) => `using it for ${m} ${m === 1 ? 'month' : 'months'}`,
      sheet: 'sheet',
      and: '&',
      note: 'Anonymised figures from the app\'s usage statistics, updated automatically. Shop names are withheld.',
    },
    cta: {
      title: 'Ready to Transform Your Workshop?',
      subtitle: 'Start your free trial today. No credit card required. 5-day free trial included.',
      btn1: 'Download Now',
      btn2: 'View Pricing',
    },
  },
}

const featureIcons = [
  <Calculator className="h-8 w-8" />,
  <FileText className="h-8 w-8" />,
  <Zap className="h-8 w-8" />,
  <DollarSign className="h-8 w-8" />,
  <FileText className="h-8 w-8" />,
  <Shield className="h-8 w-8" />,
]

const benefitIcons = [
  <TrendingUp className="h-12 w-12" />,
  <Users className="h-12 w-12" />,
  <Clock className="h-12 w-12" />,
]

const MATERIAL_NAMES = {
  en: { Steel: 'steel', Stainless: 'stainless', Aluminum: 'aluminium', Aluminium: 'aluminium', Galvanized: 'galvanised', Copper: 'copper', Brass: 'brass' },
  el: { Steel: 'μαύρη', Stainless: 'ανοξείδωτη', Aluminum: 'αλουμίνιο', Aluminium: 'αλουμίνιο', Galvanized: 'γαλβανιζέ', Copper: 'χαλκός', Brass: 'ορείχαλκος' },
}
const COUNTRY_NAMES_EL = { Greece: 'Ελλάδα', Cyprus: 'Κύπρος' }
const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1)

// Anonymised real-customer cards built from /api/stats/public `shops` —
// replaces invented testimonials with what shops actually do in the app.
const ShopProfiles = ({ shops, lang, t }) => {
  const locale = lang === 'el' ? 'el-GR' : 'en-US'
  const num = (n) => n.toLocaleString(locale)
  const mm = (n) => n.toLocaleString(locale, { maximumFractionDigits: 2 })
  const eur = (n) => n.toLocaleString(locale, { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })
  const weight = (kg) => kg >= 1000 ? `${n1(kg / 1000)} t` : `${num(kg)} kg`
  const n1 = (n) => n.toLocaleString(locale, { maximumFractionDigits: 1 })
  // A lone shop gets one wide card with more detail instead of a sparse grid
  const featured = shops.length === 1
  return (
    <section className="section-container bg-white">
      <div className="text-center mb-16">
        <h2 className="text-4xl font-bold mb-4">{t.title}</h2>
        <p className="text-xl text-gray-600 max-w-2xl mx-auto">{t.subtitle}</p>
      </div>
      <div className={featured ? 'max-w-3xl mx-auto' : 'grid grid-cols-1 lg:grid-cols-3 gap-8'}>
        {shops.map((shop, index) => {
          const materials = shop.materials.map((m) => MATERIAL_NAMES[lang][m] || m).join(` ${t.and} `)
          const thickness = shop.thicknessMin == null ? ''
            : shop.thicknessMin === shop.thicknessMax ? `${mm(shop.thicknessMin)} mm`
            : `${mm(shop.thicknessMin)}–${mm(shop.thicknessMax)} mm`
          const country = lang === 'el' ? COUNTRY_NAMES_EL[shop.country] || shop.country : shop.country
          return (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="card flex flex-col"
            >
              <div className="flex items-center gap-3 mb-4">
                <Factory className="h-8 w-8 text-primary-600" />
                <div className="font-semibold text-gray-900">{t.shop}{country && ` · ${country}`}</div>
              </div>
              {materials && (
                <p className="text-gray-700 mb-6">
                  {capitalize(lang === 'el' ? `${t.sheet} ${materials}` : `${materials} ${t.sheet}`)}
                  {thickness && <>, <span className="whitespace-nowrap">{thickness}</span></>}
                </p>
              )}
              <div className="mt-auto">
                <div className={`grid gap-6 mb-6 ${featured ? 'grid-cols-1 sm:grid-cols-3' : 'grid-cols-2'}`}>
                  <div>
                    <div className={`${featured ? 'text-4xl' : 'text-3xl'} font-bold text-primary-600`}>≈ {num(shop.offersPerMonth)}</div>
                    <div className="text-sm text-gray-600">{t.perMonth}</div>
                  </div>
                  {shop.valueEur > 0 && (
                    <div>
                      <div className={`${featured ? 'text-4xl' : 'text-3xl'} font-bold text-primary-600`}>{eur(shop.valueEur)}</div>
                      <div className="text-sm text-gray-600">{t.valueQuoted}</div>
                    </div>
                  )}
                  {featured && shop.weightKg > 0 && (
                    <div>
                      <div className="text-4xl font-bold text-primary-600">{weight(shop.weightKg)}</div>
                      <div className="text-sm text-gray-600">{t.weightQuoted}</div>
                    </div>
                  )}
                </div>
                <div className="text-sm text-gray-500 space-y-1">
                  <div>
                    {num(shop.offers)} {t.offers}
                    {shop.batches != null && ` · ${num(shop.batches)} ${t.batches}`}
                    {!featured && shop.weightKg > 0 && ` · ${weight(shop.weightKg)} ${t.quoted}`}
                  </div>
                  <div>{t.using(shop.months)}</div>
                </div>
              </div>
            </motion.div>
          )
        })}
      </div>
      <p className="text-sm text-gray-500 text-center mt-8">{t.note}</p>
    </section>
  )
}

const Home = () => {
  const { lang } = useLanguage()
  const t = translations[lang]
  const stats = useLiveStats()

  return (
    <div className="overflow-hidden">
      <SEO
        title="Sheet Metal & Manufacturing Cost Estimator Software"
        description="Costing software for metal fabrication and CNC shops: calculate material, laser cutting, bending and CNC router milling costs from DXF & STEP files and create quotes in seconds. Free trial."
        path="/"
      />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
        <div className="section-container relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                {t.hero.title}
                <span className="text-primary-400">{t.hero.highlight}</span>
              </h1>
              <p className="text-xl text-gray-300 mb-8 leading-relaxed">{t.hero.subtitle}</p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/download"
                  className="flex items-center justify-center space-x-2 px-8 py-4 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  <Download className="h-5 w-5" />
                  <span>{t.hero.cta1}</span>
                </Link>
                <Link
                  to="/features"
                  className="flex items-center justify-center space-x-2 px-8 py-4 bg-white text-gray-900 rounded-lg font-semibold hover:bg-gray-100 transition-all duration-200"
                >
                  <span>{t.hero.cta2}</span>
                  <ArrowRight className="h-5 w-5" />
                </Link>
              </div>
              <div className="mt-8 flex items-center space-x-6 text-sm">
                {[t.hero.badge1, t.hero.badge2, t.hero.badge3].map((badge, i) => (
                  <div key={i} className="flex items-center space-x-2">
                    <Check className="h-5 w-5 text-green-400" />
                    <span>{badge}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="rounded-2xl shadow-2xl overflow-hidden border border-gray-700">
                <img
                  src={`${import.meta.env.BASE_URL}screenshots/offers.png?v=2`}
                  alt="Workshop Assistant showing a detailed cost offer breakdown"
                  className="w-full h-auto block"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-white rounded-xl shadow-xl p-6 border-2 border-primary-500">
                <div className="text-3xl font-bold text-primary-600 tabular-nums">
                  <CountUp value={stats?.offers} lang={lang} />
                </div>
                <div className="text-sm text-gray-600">{t.hero.stat}</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <LiveStats stats={stats} lang={lang} />

      {/* Features Grid */}
      <section className="section-container bg-white">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold mb-4">{t.featuresSection.title}</h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">{t.featuresSection.subtitle}</p>
          <Link to="/sheet-metal-costing" className="inline-flex items-center gap-2 mt-4 font-semibold text-primary-600 hover:text-primary-700">
            {t.featuresSection.guide}<ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {t.features.map((feature, index) => {
            const CardTag = featureLinks[index] ? Link : 'div'
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <CardTag
                  {...(featureLinks[index] ? { to: featureLinks[index] } : {})}
                  className="card group hover:border-primary-500 border-2 border-transparent block h-full"
                >
                  <div className="text-primary-600 mb-4 group-hover:scale-110 transition-transform duration-200">
                    {featureIcons[index]}
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </CardTag>
              </motion.div>
            )
          })}
        </div>
      </section>

      {/* Benefits Section */}
      <section className="section-container bg-gray-50">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold mb-4">{t.benefitsSection.title}</h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">{t.benefitsSection.subtitle}</p>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {t.benefits.map((benefit, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-2xl shadow-xl p-8 text-center"
            >
              <div className="inline-flex items-center justify-center w-20 h-20 bg-primary-100 text-primary-600 rounded-full mb-6">
                {benefitIcons[index]}
              </div>
              <div className="text-4xl font-bold text-primary-600 mb-2">{benefit.stat}</div>
              <div className="text-sm text-gray-500 mb-4">{benefit.statLabel}</div>
              <h3 className="text-2xl font-semibold mb-4">{benefit.title}</h3>
              <p className="text-gray-600">{benefit.description}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {stats?.shops?.length > 0 && <ShopProfiles shops={stats.shops} lang={lang} t={t.shopsSection} />}

      {/* CTA Section */}
      <section className="gradient-primary text-white">
        <div className="section-container max-w-4xl text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">{t.cta.title}</h2>
          <p className="text-xl mb-8 opacity-90">{t.cta.subtitle}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/download"
              className="flex items-center justify-center space-x-2 px-8 py-4 bg-white text-primary-600 rounded-lg font-semibold hover:bg-gray-100 transition-all duration-200 shadow-lg"
            >
              <Download className="h-5 w-5" />
              <span>{t.cta.btn1}</span>
            </Link>
            <Link
              to="/pricing"
              className="flex items-center justify-center space-x-2 px-8 py-4 bg-transparent border-2 border-white text-white rounded-lg font-semibold hover:bg-white hover:text-primary-600 transition-all duration-200"
            >
              <span>{t.cta.btn2}</span>
              <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Home
